import { IScope, IRootScopeService, ITimeoutService, IPromise, IAngularEvent } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalService } from "angular-ui-bootstrap";
import { AssigneeActions, IAssigneeIdMap, IAssigneesStoreState } from "@gtmhub/assignees";
import { IStateInit } from "@gtmhub/core/routing";
import { IEmployee, ICurrentEmployeeStoreState, CurrentEmployeeActions } from "@gtmhub/employees";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IHubFilter } from "@gtmhub/hub/models/hub";
import { HubService } from "@gtmhub/hub/services/hub";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { PageTitleService } from "@gtmhub/shared/services/pageTitleService";
import { INgRedux } from "@gtmhub/state-management";
import { ITeamsIdMap, ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { localize } from "@gtmhub/localization";
import { capitalize } from "@gtmhub/util";

interface ITaskFilterStateParams {
  filterId?: string;
}

const bindStateToScope = (state: ICurrentEmployeeStoreState & IAssigneesStoreState & ITeamsStoreState): ITaskFilterRedux => {
  const { isFetched: assigneesFetched, error: assigneesError, map: assigneesIdMap } = state.assignees;
  const { isFetched: teamsFetched, error: teamsError, map: teamsIdMap } = state.teams;
  return {
    employee: state.currentEmployee.info,
    assigneesIdMap,
    teamsIdMap,
    assigneesFetched,
    assigneesError,
    teamsFetched,
    teamsError,
  };
};

interface ITaskFilterRedux {
  assigneesIdMap: IAssigneeIdMap;
  assigneesFetched: boolean;
  assigneesError: IUIError;
  teamsIdMap: ITeamsIdMap;
  teamsFetched: boolean;
  teamsError: IUIError;
  employee: IEmployee;
}

interface ITaskFilterScope extends IScope, IExtendedRootScopeService, ITaskFilterRedux {
  deleteFilter(filterId: string): void;
  editFilter(filterId: string): void;
  assignees: string[];
  indicators: {
    loadingScreen: boolean;
    updating: boolean;
    gettingAssignees: boolean;
    creatingFilter: boolean;
    deletingFilter: boolean;
    patchingFilter: boolean;
  };

  filter: Pick<IHubFilter, "name">;
  params: ITaskFilterStateParams;
}

export class TaskFilterCtrl implements IStateInit {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "$uibModal",
    "$timeout",
    "HubService",
    "UIErrorHandlingService",
    "PageTitleService",
    "$ngRedux",
    "CurrentEmployeeActions",
    "AssigneeActions",
    "TeamsActions",
  ];

  constructor(
    private $rootScope: IRootScopeService,
    private $scope: ITaskFilterScope,
    private $state: StateService,
    private $stateParams: ITaskFilterStateParams,
    private $uibModal: IModalService,
    private $timeout: ITimeoutService,
    private hubService: HubService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private pageTitleService: PageTitleService,
    private $ngRedux: INgRedux,
    private currentEmployeeActions: CurrentEmployeeActions,
    private assigneeActions: AssigneeActions,
    private teamsActions: TeamsActions
  ) {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.$scope.indicators = {
      loadingScreen: false,
      updating: false,
      gettingAssignees: false,
      creatingFilter: false,
      deletingFilter: false,
      patchingFilter: false,
    };

    this.$scope.params = { ...this.$stateParams };

    this.$scope.editFilter = this.editFilter;
    this.$scope.deleteFilter = this.deleteFilter;

    this.$scope.$on("filterChanged", (evt: IAngularEvent, filter: IHubFilter) => this.onFilterChanged(filter));
  }

  public stateInit(): IPromise<unknown> {
    if (this.$stateParams.filterId) {
      this.onLoadFilter(this.$stateParams.filterId);
    } else {
      this.onSetFakeFilter();
    }

    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.$scope.indicators.loadingScreen = true;

    this.$ngRedux.dispatch(this.currentEmployeeActions.fetchCurrentEmployeeIfMissing());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());

    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.teamsFetched,
        () => this.$scope.teamsError
      )
      .then(
        () => {
          this.$scope.indicators.loadingScreen = false;
        },
        () => this.uiErrorHandlingService.handleIndicator(this.$scope.teamsError, { indicator: "loadingScreen", inline: true })
      );
  };

  private onLoadFilter = (filterId: string) => {
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.assigneesFetched,
        () => this.$scope.assigneesError
      )
      .then(
        () => {
          this.hubService.getFilter(filterId).then(
            (filter) => {
              if (!filter.teamIds) {
                filter.teamIds = [];
              }
              if (!filter.employeeIds) {
                filter.employeeIds = [];
              }

              this.$scope.assignees = filter.employeeIds.concat(filter.teamIds);
              this.$scope.filter = filter;
              this.pageTitleService.setPageTitle(`${this.$scope.filter.name}`);
            },
            (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "loading" })
          );
        },
        () => this.uiErrorHandlingService.handleIndicator(this.$scope.assigneesError, { indicator: "loading" })
      );
  };

  private onSetFakeFilter = () => {
    this.$scope.filter = { name: capitalize(localize("my_tasks")) };
    this.pageTitleService.setPageTitle(`${this.$scope.filter.name}`);
  };

  private onFilterChanged = (filter: IHubFilter) => {
    if (this.$scope.params.filterId && filter.id === this.$scope.params.filterId) {
      this.onLoadFilter(filter.id);
    }
  };

  private setDynamicName = (filterName: string) => {
    if (this.$scope.teamsIdMap) {
      this.$scope.filter = { name: this.$scope.teamsIdMap[filterName].name };
      this.pageTitleService.setPageTitle(`${this.$scope.filter.name}`);
    } else {
      this.$timeout(() => {
        this.setDynamicName(filterName);
      });
    }
  };

  private editFilter = (filterId: string) => {
    this.$state.go(this.$state.current.name + "._filter.filter", { formFilterId: filterId });
  };

  private deleteFilter = (filterId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../../hub/views/confirm-delete.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        "$uibModalInstance",
        function ($scope, $uibModalInstance) {
          $scope.close = () => {
            $uibModalInstance.close();
          };

          $scope.delete = () => {
            ctrl.$scope.indicators.deletingFilter = true;

            if (!filterId) {
              throw new Error("No filter with this id exists. Please try again!");
            }

            const scopeParams = {
              filterId: null,
            };

            ctrl.$scope.indicators.deletingFilter = true;

            ctrl.hubService.deleteFilter(filterId).then(
              () => {
                ctrl.$rootScope.$broadcast("filtersChanged", true);
                ctrl.$state.go("gtmhub.tasks", scopeParams);
                ctrl.$scope.filter.name = "My Tasks";

                ctrl.$scope.indicators.deletingFilter = false;
              },
              (error) => ctrl.uiErrorHandlingService.handleIndicator(error, { indicator: "deletingFilter", inline: true })
            );

            $uibModalInstance.close();
          };
        },
      ],
    });
  };
}
